import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { APPLIES_TO, PROMOTION_EFFECT_KINDS } from '@lensmira/domain'
import { formatDay } from '@lensmira/i18n'

import { DataSurface } from './DataSurface.js'
import { Ltr, useTranslation } from '../i18n.js'
import {
  MoneyInputError,
  formatMinorForInput,
  formatWireForDisplay,
  parseMajorInput,
  parseMinorUnits,
} from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import { appliesToLabel, promotionEffectLabel, promotionStatusWord } from '../status-labels.js'
import { IssueCoupon } from './IssueCoupon.js'
import {
  ConditionBuilder,
  ConditionInputError,
  rowsFromCondition,
  toCondition,
  type ConditionRow,
} from './condition-builder.js'

/**
 * Promotions. §6.25 `PRM-001` to `PRM-007`, Appendix X.
 *
 * **A promotion is a rule, and the screen builds the rule rather than asking for one.** `PRM-001`
 * gives six effects and Appendix X gives a closed field registry, so what a shop can offer is a
 * bounded set of shapes: this many percent off, this much off, this fixed price, a free item, a
 * free treatment, an upgrade. The condition is `condition-builder.tsx` and the same
 * `validateCondition` the server runs is run here, so a rule that could never match is refused in
 * the place somebody can fix it.
 *
 * **A published version is frozen, so changing one writes a new version.** `PRM-001` versions a
 * promotion and `X.4` makes an order store which rule version applied to it, which is the whole
 * reason a published row cannot be edited in place: the sale that was charged under version 2 has
 * to stay explicable after version 3 exists. Publishing version 3 withdraws version 2 with
 * "Superseded by version 3", which the server does and this screen does not duplicate.
 *
 * **Withdrawing asks why, and the server refuses a blank.** `PRM-001`'s record is what explains a
 * discount a customer remembers getting last week and cannot get today.
 *
 * **The margin floor is `PRM-004` and is gated.** It arrives only for a reader with Appendix B's
 * margin grant at that branch, so an absent floor means withheld rather than none, and the screen
 * says nothing about it rather than printing a zero.
 */

export interface PromotionEffectData {
  readonly kind: string
  readonly percent?: string
  readonly amountMinor?: string
  readonly appliesTo?: string
  readonly productId?: string
  readonly category?: string
  readonly treatmentId?: string
  readonly fromTier?: string
  readonly toTier?: string
}

export interface PromotionData {
  readonly promotionId: string
  readonly versionNumber: number
  readonly version: number
  readonly branchId: string
  /** `draft`, `published` or `withdrawn`. Unnarrowed per `ARC-020`. */
  readonly status: string
  readonly label: string
  readonly condition: unknown
  readonly effect: PromotionEffectData
  readonly priority: number
  readonly stackable: boolean
  readonly startsOn: string
  readonly endsOn: string
  readonly requiresCoupon: boolean
  /** Every amount on this offer is in it. Never read out of the gated floor. */
  readonly currency: string
  /** `PRM-004`'s floor, absent when there is none or when it is withheld. */
  readonly marginFloorMinor?: string
  /**
   * `ROLE-001a`: the floor exists and this reader may not see it.
   *
   * The two readings of an absent floor have opposite consequences. With this, a revision can
   * refuse rather than write a version with no floor, which is what it did: the screen turned the
   * withheld field into a blank input and `PRM-004`'s gate was erased by somebody who could not
   * read it.
   */
  readonly marginFloorWithheld: boolean
  readonly withdrawReason?: string
}

export interface PromotionsProps {
  /**
   * The branch's timezone, for `LOC-004`.
   *
   * Appendix J: an instant renders in the branch's zone, never the browser's. A plain `date`
   * column renders in none, which is what `formatDay` is for.
   */
  readonly timezone: string
  readonly load: () => Promise<readonly PromotionData[]>
  readonly branchId: string
  /** The shop's own currency, for every amount on this screen. Never guessed. */
  readonly currency: string
  /**
   * The branch's own today, as `YYYY-MM-DD`, for deciding whether a window has passed.
   *
   * From the caller rather than from `new Date()` here: the server compares against the branch's
   * local date (`branchToday`), and a device whose clock or zone is wrong would hide a publish
   * button that works or offer one that is refused.
   */
  readonly today: string
}

/**
 * The condition for an offer that applies to every sale.
 *
 * Appendix X has no "always" node and `parseRule` refuses an absent condition, naming this exact
 * form in its own refusal: "A promotion that applies to every sale says so, for example
 * order.line_count >= 1." The screen used to omit the field and call that "always applies", which
 * the server answered with `FIELD_REQUIRED`, and a refused write stops the whole queue.
 */
const ALWAYS = { field: 'order.line_count', op: '>=', value: 1 } as const

interface Form {
  readonly label: string
  readonly kind: string
  readonly percent: string
  readonly amount: string
  readonly appliesTo: string
  readonly productId: string
  readonly category: string
  readonly treatmentId: string
  readonly fromTier: string
  readonly toTier: string
  readonly startsOn: string
  readonly endsOn: string
  readonly priority: string
  readonly stackable: boolean
  readonly requiresCoupon: boolean
  readonly marginFloor: string
  /** `PRM-006`. False scopes the offer to the branch it is raised at. */
  readonly everyBranch: boolean
  readonly rows: readonly ConditionRow[]
  /** Carried through a revision untouched when the builder cannot express it. */
  readonly keepCondition?: unknown
}

const EMPTY_FORM: Form = {
  label: '',
  kind: 'percent_off',
  percent: '',
  amount: '',
  appliesTo: 'line',
  productId: '',
  category: '',
  treatmentId: '',
  fromTier: '',
  toTier: '',
  startsOn: '',
  endsOn: '',
  priority: '0',
  stackable: false,
  requiresCoupon: false,
  marginFloor: '',
  // This branch by default: an offer raised at a counter is that shop's offer, and the server
  // records `promotion.branch_id` accordingly. A tenant-wide offer is a deliberate choice.
  everyBranch: false,
  rows: [],
}

export function Promotions({
  load,
  branchId,
  currency,
  today,
  timezone,
}: PromotionsProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [rows, setRows] = useState<readonly PromotionData[] | null>(null)
  const [form, setForm] = useState<Form>(EMPTY_FORM)
  /** The promotion a new version is being written for, or null for a new promotion. */
  const [revising, setRevising] = useState<string | null>(null)
  const [reasons, setReasons] = useState<Record<string, string>>({})
  // Form and write problems only. A failed read has its own variable because the two are different
  // states: a mistyped label must not blank the list, and a dead server must not read as a typo.
  const [error, setError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setRows(await load())
      setLoadError(null)
    } catch {
      setLoadError(t('promotions.loadFailed'))
    }
  }, [load, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setForm(EMPTY_FORM)
    setRevising(null)
    setReasons({})
    void refresh()
  })

  const effectWords = (row: PromotionData): string => {
    const amount =
      row.effect.amountMinor === undefined
        ? ''
        : formatWireForDisplay(row.effect.amountMinor, row.currency)
    switch (row.effect.kind) {
      case 'percent_off':
        return t('promotions.effect.percentOff', { percent: row.effect.percent ?? '' })
      case 'amount_off':
        return t('promotions.effect.amountOff', { amount })
      case 'fixed_price':
        return t('promotions.effect.fixedPrice', { amount })
      case 'free_item':
        return t('promotions.effect.freeItem', {
          what: row.effect.productId ?? row.effect.category ?? '',
        })
      case 'free_treatment':
        return t('promotions.effect.freeTreatment', { what: row.effect.treatmentId ?? '' })
      case 'upgrade':
        return t('promotions.effect.upgrade', {
          from: row.effect.fromTier ?? '',
          to: row.effect.toTier ?? '',
        })
      default:
        // `ARC-020`: an effect this build has no words for shows as itself.
        return row.effect.kind
    }
  }

  const effectBody = (): Record<string, unknown> => {
    const minor = (text: string): string => parseMajorInput(text, currency).toString()
    switch (form.kind) {
      case 'percent_off':
        return { kind: 'percent_off', percent: form.percent.trim(), appliesTo: form.appliesTo }
      case 'amount_off':
        return {
          kind: 'amount_off',
          amountMinor: minor(form.amount),
          appliesTo: form.appliesTo,
        }
      case 'fixed_price':
        return { kind: 'fixed_price', amountMinor: minor(form.amount) }
      case 'free_item':
        // The codec refuses both or neither, so exactly one is sent and the form asks for one.
        return form.productId.trim() === ''
          ? { kind: 'free_item', category: form.category.trim() }
          : { kind: 'free_item', productId: form.productId.trim() }
      case 'free_treatment':
        return { kind: 'free_treatment', treatmentId: form.treatmentId.trim() }
      default:
        return {
          kind: 'upgrade',
          fromTier: form.fromTier.trim(),
          toTier: form.toTier.trim(),
        }
    }
  }

  const save = useCallback(async () => {
    if (form.label.trim() === '') {
      setError(t('promotions.labelRequired'))
      return
    }
    if (form.startsOn === '' || form.endsOn === '') {
      // `PRM-001`'s window, both ends required and both inclusive. A promotion with no end is a
      // margin leak by omission, which the domain says in as many words.
      setError(t('promotions.windowRequired'))
      return
    }
    const priority = Number(form.priority)
    if (!Number.isInteger(priority)) {
      setError(t('promotions.priorityWhole'))
      return
    }

    let condition: unknown
    let effect: Record<string, unknown>
    let marginFloorMinor: string | undefined
    try {
      condition =
        form.rows.length === 0 && form.keepCondition !== undefined
          ? form.keepCondition
          : (toCondition(form.rows, currency) ?? ALWAYS)
      effect = effectBody()
      marginFloorMinor =
        form.marginFloor.trim() === ''
          ? undefined
          : parseMajorInput(form.marginFloor, currency).toString()
    } catch (inputError) {
      setError(
        inputError instanceof ConditionInputError || inputError instanceof MoneyInputError
          ? inputError.message
          : t('promotions.cannotSave'),
      )
      return
    }

    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: revising === null ? '/promotions' : `/promotions/${revising}/versions`,
      label: revising === null ? t('promotions.queueNewLabel') : t('promotions.queueReviseLabel'),
      body: {
        ...(revising === null ? { branchId } : {}),
        label: form.label.trim(),
        condition,
        effect,
        priority,
        stackable: form.stackable,
        window: { startsOn: form.startsOn, endsOn: form.endsOn },
        requiresCoupon: form.requiresCoupon,
        ...(marginFloorMinor === undefined ? {} : { marginFloorMinor }),
        /**
         * `PRM-006`: an absent scope dimension is unrestricted, so an offer with no scope applies
         * at every branch in the tenant that shares its currency. The rules-in-force query filters
         * on currency alone, by design, and nothing defaults the scope to the branch the offer was
         * raised at. A screen that sent nothing therefore made every offer tenant-wide, which is
         * not what a shopkeeper means by an offer at their counter, so the choice is explicit.
         */
        ...(form.everyBranch ? {} : { scope: { branchIds: [branchId] } }),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [branchId, currency, enqueue, form, revising, t])

  const publish = useCallback(
    async (row: PromotionData) => {
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/promotions/${row.promotionId}/versions/${String(row.versionNumber)}/publication`,
        label: t('promotions.queuePublishLabel'),
        body: { expectedVersion: row.version },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, t],
  )

  const withdraw = useCallback(
    async (row: PromotionData) => {
      const key = `${row.promotionId}|${String(row.versionNumber)}`
      const reason = reasons[key]?.trim() ?? ''
      if (reason === '') {
        // The server refuses a blank, and the reason is what explains a discount a customer got
        // last week and cannot get today.
        setError(t('promotions.reasonRequired'))
        return
      }
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/promotions/${row.promotionId}/versions/${String(row.versionNumber)}/withdrawal`,
        label: t('promotions.queueWithdrawLabel'),
        body: { expectedVersion: row.version, reason },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, reasons, t],
  )

  const startRevision = (row: PromotionData): void => {
    if (row.marginFloorWithheld) {
      /**
       * `PRM-004` and `ROLE-001a` together.
       *
       * A revision replaces the whole rule, and this reader cannot see the floor, so whatever they
       * saved would publish a version with no floor at all: the gate erased by somebody who could
       * not read it. Refused rather than warned, because a warning on a form still lets the write
       * through, and `CLAUDE.md` says a gate is impossible rather than discouraged.
       */
      setError(t('promotions.cannotReviseFloor'))
      return
    }
    const existing = rowsFromCondition(row.condition, currency)
    setRevising(row.promotionId)
    setError(existing === null ? t('promotions.conditionKept') : null)
    setForm({
      label: row.label,
      kind: row.effect.kind,
      percent: row.effect.percent ?? '',
      // Bare digits, which is what the money input takes: the grouped display form comes back
      // from `parseMajorInput` as "Not a valid amount".
      amount:
        row.effect.amountMinor === undefined
          ? ''
          : formatMinorForInput(parseMinorUnits(row.effect.amountMinor), row.currency),
      appliesTo: row.effect.appliesTo ?? 'line',
      productId: row.effect.productId ?? '',
      category: row.effect.category ?? '',
      treatmentId: row.effect.treatmentId ?? '',
      fromTier: row.effect.fromTier ?? '',
      toTier: row.effect.toTier ?? '',
      startsOn: row.startsOn,
      endsOn: row.endsOn,
      priority: String(row.priority),
      stackable: row.stackable,
      requiresCoupon: row.requiresCoupon,
      marginFloor:
        row.marginFloorMinor === undefined
          ? ''
          : formatMinorForInput(parseMinorUnits(row.marginFloorMinor), row.currency),
      rows: existing ?? [],
      // The scope is not on the view, so a revision cannot read the old one. Defaulting to this
      // branch matches what a new offer does and what the promotion's own `branch_id` says, and
      // `later_todo.md` carries the gap: `PRM-006`'s scope should be readable before it is
      // rewritten.
      everyBranch: false,
      // A condition the builder cannot express is carried through rather than flattened: an `any`
      // read as an `all` would charge different prices.
      ...(existing === null ? { keepCondition: row.condition } : {}),
    })
  }

  const line = statusLine(t, snapshot, writeId, t('queue.subject.promotion'))

  return (
    <section className="lm-screen" aria-labelledby="promotions-heading">
      <h1 id="promotions-heading" className="lm-screen__heading">
        {t('promotions.heading')}
      </h1>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {/*
        The form below stays rendered in every state: it is how the first offer gets written, so a
        surface that swallowed it would be most in the way exactly when the list is empty. Partial
        is real here, a manager without the margin grant gets every offer but not its floor (the
        row says so too, this is the banner above them).
      */}
      <DataSurface
        state={
          loadError !== null && rows === null
            ? {
                kind: 'error',
                message: loadError,
                onRetry: () => {
                  setLoadError(null)
                  void refresh()
                },
              }
            : rows === null
              ? { kind: 'loading' }
              : rows.length === 0
                ? { kind: 'empty' }
                : // **No `partial` here, deliberately, and it had one.** The banner read "Some of
                  // this is not shown: the lowest margin on some offers", which is a summary of
                  // something every affected row already says for itself a few lines below
                  // (`promotions.floorWithheld`, "Has a lowest margin you cannot see"). Of the two
                  // the per-row line is the one that cannot go: it is what explains why that row's
                  // revise button refuses. A banner repeating it adds a sentence and no fact.
                  //
                  // `ST-02`'s `partial` is for a surface where something is missing and nothing on
                  // the surface says which part, which is `Purchasing`'s case: a dispenser gets the
                  // rows minus a column with no per-row marker at all.
                  { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('promotions.what')}
        emptyMessage={t('promotions.empty')}
      >
        <ul className="lm-promotion-list">
          {(rows ?? []).map((row) => {
            const key = `${row.promotionId}|${String(row.versionNumber)}`
            return (
              <li key={key} className="lm-promotion">
                <span className="lm-promotion__label">{row.label}</span>
                <span className="lm-promotion__version" dir="ltr">
                  <Ltr>{`v${String(row.versionNumber)}`}</Ltr>
                </span>
                <span className="lm-promotion__status">{promotionStatusWord(t, row.status)}</span>
                <span className="lm-promotion__effect">{effectWords(row)}</span>
                <span className="lm-promotion__window">
                  {sentence(t, 'promotions.window', {
                    from: <Ltr>{formatDay(row.startsOn, timezone, locale)}</Ltr>,
                    to: <Ltr>{formatDay(row.endsOn, timezone, locale)}</Ltr>,
                  })}
                </span>

                {row.stackable ? null : (
                  // `PRM-003`: a promotion that does not combine suppresses the lower-priority ones
                  // touching the same line, which is why it is on the row and not in a detail view.
                  <span className="lm-promotion__alone">{t('promotions.notWithOthers')}</span>
                )}

                {row.requiresCoupon ? (
                  <span className="lm-promotion__coupon">{t('promotions.needsCoupon')}</span>
                ) : null}

                {row.marginFloorMinor === undefined ? null : (
                  <span className="lm-promotion__floor" dir="ltr">
                    {t('promotions.floor', {
                      amount: formatWireForDisplay(row.marginFloorMinor, row.currency),
                    })}
                  </span>
                )}

                {row.marginFloorWithheld ? (
                  // Said rather than silent: this offer has a floor and this reader may not see it,
                  // which is why the revise button below refuses.
                  <span className="lm-promotion__floor-withheld">
                    {t('promotions.floorWithheld')}
                  </span>
                ) : null}

                {row.withdrawReason === undefined ? null : (
                  <span className="lm-promotion__withdrawn">
                    {t('promotions.withdrawnBecause', { reason: row.withdrawReason })}
                  </span>
                )}

                {row.status === 'draft' && row.endsOn >= today ? (
                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    onClick={() => void publish(row)}
                  >
                    {t('promotions.publish')}
                  </button>
                ) : null}

                {row.status === 'draft' && row.endsOn < today ? (
                  // The server refuses this with `PROMOTION_WINDOW_PASSED`, and a refused write
                  // stops the queue, so the screen says it rather than offering the button. Both
                  // dates are branch-local `YYYY-MM-DD`, so the string comparison is the date one.
                  <span className="lm-promotion__too-late">{t('promotions.windowPassed')}</span>
                ) : null}

                {/* Published or draft. `withdraw` refuses only a version that is already
                    withdrawn, and an abandoned draft that cannot be retired keeps a publish button
                    that would supersede the live version with the wrong rule. */}
                {row.status === 'published' || row.status === 'draft' ? (
                  <>
                    <label className="lm-field lm-field--inline">
                      <span className="lm-field__label">{t('promotions.reasonLabel')}</span>
                      <input
                        className="lm-input"
                        autoComplete="off"
                        value={reasons[key] ?? ''}
                        onChange={(e) => {
                          const value = e.currentTarget.value
                          setReasons((previous) => ({ ...previous, [key]: value }))
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => void withdraw(row)}
                    >
                      {t('promotions.withdraw')}
                    </button>
                  </>
                ) : null}

                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => {
                    startRevision(row)
                  }}
                >
                  {t('promotions.revise')}
                </button>

                {/* `PRM-005`: a coupon belongs to the offer it unlocks, so it is issued from the
                    offer's own row rather than from a list of codes with nothing to attach to. */}
                <IssueCoupon promotionId={row.promotionId} promotionLabel={row.label} />
              </li>
            )
          })}
        </ul>
      </DataSurface>

      <h2 className="lm-promotion-form__heading">
        {revising === null ? t('promotions.newHeading') : t('promotions.reviseHeading')}
      </h2>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('promotions.labelLabel')}</span>
        <input
          className="lm-input"
          autoComplete="off"
          value={form.label}
          onChange={(e) => {
            const value = e.currentTarget.value
            setForm((previous) => ({ ...previous, label: value }))
          }}
        />
      </label>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('promotions.kindLabel')}</span>
        <select
          className="lm-input"
          value={form.kind}
          onChange={(e) => {
            const value = e.currentTarget.value
            setForm((previous) => ({ ...previous, kind: value }))
          }}
        >
          {/* `PRM-001`'s six, from the domain's own list rather than a copy of it. */}
          {PROMOTION_EFFECT_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {promotionEffectLabel(t, kind)}
            </option>
          ))}
        </select>
      </label>

      {form.kind === 'percent_off' ? (
        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('promotions.percentLabel')}</span>
          <input
            className="lm-input"
            inputMode="decimal"
            autoComplete="off"
            value={form.percent}
            onChange={(e) => {
              const value = e.currentTarget.value
              setForm((previous) => ({ ...previous, percent: value }))
            }}
          />
        </label>
      ) : null}

      {form.kind === 'amount_off' || form.kind === 'fixed_price' ? (
        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('promotions.amountLabel', { currency })}</span>
          <input
            className="lm-input"
            inputMode="decimal"
            autoComplete="off"
            value={form.amount}
            onChange={(e) => {
              const value = e.currentTarget.value
              setForm((previous) => ({ ...previous, amount: value }))
            }}
          />
        </label>
      ) : null}

      {form.kind === 'percent_off' || form.kind === 'amount_off' ? (
        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('promotions.appliesToLabel')}</span>
          <select
            className="lm-input"
            value={form.appliesTo}
            onChange={(e) => {
              const value = e.currentTarget.value
              setForm((previous) => ({ ...previous, appliesTo: value }))
            }}
          >
            {APPLIES_TO.filter(
              // An amount off a category has no defined meaning and the domain refuses it, so it
              // is not offered: the spec does not say whether the amount comes off once across the
              // category or off every line in it, and the two differ by the number of lines.
              (applies) => !(form.kind === 'amount_off' && applies === 'category'),
            ).map((applies) => (
              <option key={applies} value={applies}>
                {appliesToLabel(t, applies)}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {form.kind === 'free_item' ? (
        <>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('promotions.productIdLabel')}</span>
            <input
              className="lm-input"
              autoComplete="off"
              value={form.productId}
              onChange={(e) => {
                const value = e.currentTarget.value
                setForm((previous) => ({ ...previous, productId: value }))
              }}
            />
          </label>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('promotions.categoryLabel')}</span>
            <input
              className="lm-input"
              autoComplete="off"
              value={form.category}
              onChange={(e) => {
                const value = e.currentTarget.value
                setForm((previous) => ({ ...previous, category: value }))
              }}
            />
          </label>
          <p className="lm-hint">{t('promotions.oneOfTwo')}</p>
        </>
      ) : null}

      {form.kind === 'free_treatment' ? (
        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('promotions.treatmentIdLabel')}</span>
          <input
            className="lm-input"
            autoComplete="off"
            value={form.treatmentId}
            onChange={(e) => {
              const value = e.currentTarget.value
              setForm((previous) => ({ ...previous, treatmentId: value }))
            }}
          />
        </label>
      ) : null}

      {form.kind === 'upgrade' ? (
        <>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('promotions.fromTierLabel')}</span>
            <input
              className="lm-input"
              autoComplete="off"
              value={form.fromTier}
              onChange={(e) => {
                const value = e.currentTarget.value
                setForm((previous) => ({ ...previous, fromTier: value }))
              }}
            />
          </label>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('promotions.toTierLabel')}</span>
            <input
              className="lm-input"
              autoComplete="off"
              value={form.toTier}
              onChange={(e) => {
                const value = e.currentTarget.value
                setForm((previous) => ({ ...previous, toTier: value }))
              }}
            />
          </label>
        </>
      ) : null}

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('promotions.startsOnLabel')}</span>
        <input
          className="lm-input"
          type="date"
          value={form.startsOn}
          onChange={(e) => {
            const value = e.currentTarget.value
            setForm((previous) => ({ ...previous, startsOn: value }))
          }}
        />
      </label>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('promotions.endsOnLabel')}</span>
        <input
          className="lm-input"
          type="date"
          value={form.endsOn}
          onChange={(e) => {
            const value = e.currentTarget.value
            setForm((previous) => ({ ...previous, endsOn: value }))
          }}
        />
      </label>
      {/* Both ends inclusive, which the domain decided and a sign in a shop window implies. */}
      <p className="lm-hint">{t('promotions.windowInclusive')}</p>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('promotions.priorityLabel')}</span>
        <input
          className="lm-input"
          inputMode="numeric"
          autoComplete="off"
          value={form.priority}
          onChange={(e) => {
            const value = e.currentTarget.value
            setForm((previous) => ({ ...previous, priority: value }))
          }}
        />
      </label>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('promotions.stackableLabel')}</span>
        <input
          type="checkbox"
          checked={form.stackable}
          onChange={(e) => {
            const value = e.currentTarget.checked
            setForm((previous) => ({ ...previous, stackable: value }))
          }}
        />
      </label>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('promotions.everyBranchLabel')}</span>
        <input
          type="checkbox"
          checked={form.everyBranch}
          onChange={(e) => {
            const value = e.currentTarget.checked
            setForm((previous) => ({ ...previous, everyBranch: value }))
          }}
        />
      </label>
      {/* `PRM-006`: an absent scope is unrestricted, so this has to be a decision rather than a
          default. Left off, the offer is scoped to the branch it is raised at. */}
      <p className="lm-hint">{t('promotions.everyBranchMeans')}</p>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('promotions.requiresCouponLabel')}</span>
        <input
          type="checkbox"
          checked={form.requiresCoupon}
          onChange={(e) => {
            const value = e.currentTarget.checked
            setForm((previous) => ({ ...previous, requiresCoupon: value }))
          }}
        />
      </label>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('promotions.floorLabel', { currency })}</span>
        <input
          className="lm-input"
          inputMode="decimal"
          autoComplete="off"
          value={form.marginFloor}
          onChange={(e) => {
            const value = e.currentTarget.value
            setForm((previous) => ({ ...previous, marginFloor: value }))
          }}
        />
      </label>
      {/* `PRM-004` gives no number and no unit, so the figure is the shop's and the unit is said
          here: line margin after the promotion, before tax. */}
      <p className="lm-hint">{t('promotions.floorMeans')}</p>

      <ConditionBuilder
        rows={form.rows}
        currency={currency}
        onChange={(next) => {
          setForm((previous) => ({ ...previous, rows: next }))
        }}
      />

      <button type="button" className="lm-button lm-button--primary" onClick={() => void save()}>
        {revising === null ? t('promotions.saveNew') : t('promotions.saveRevision')}
      </button>

      {revising === null ? null : (
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            setRevising(null)
            setForm(EMPTY_FORM)
            setError(null)
          }}
        >
          {t('promotions.cancelRevision')}
        </button>
      )}
    </section>
  )
}
